import type { Context } from 'root/utils/errorReporting/LoggingHandler';
import {
    getDecodedFormFieldValue,
    isFormExcludedFromLocalStorage,
    isEnabledFormAutofill,
} from 'root/utils/dom/localStorage';
import { getFieldName } from 'root/utils/forms/getFieldData';
import { initPhoneLib } from 'root/main/components/forms/phoneInit';
import { gtm } from 'root/utils/tracking/googleTagManagerDeprecated';
import doFetch from 'root/utils/cors/doFetch';
import {
    loadCaptcha,
    recaptchaFormFieldValidation,
} from 'root/core/tracking/recaptcha/recaptcha';
import honeyPotField from 'root/main/components/forms/ui/honeyPotField';
import { handleDomainPrefixField } from 'root/helpers/formHelpers';
import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import { GoogleTagManager } from 'root/utils/tracking/GA4/googleTagManager';
import { FormGTMObjectConstructor } from 'root/utils/tracking/GA4/formTracking';
import { getAnonymizedFieldValue } from 'root/utils/forms/getAnonymizedFieldValue';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import { handleZipCodeField } from 'root/helpers/formHelpers';
import { debounce } from 'lodash';
// The functions below imported from fromHelpersTechDebt are not Typescript safe
import {
    handleGooglePlacesFieldAutocomplete,
    setDataAttributeForAddress,
    setDataAttributeForZipCode,
} from 'root/helpers/formHelpersTechDebt';
import formConstants from 'root/global_configs/formConstants.json';
import intlTelInput from 'intl-tel-input';

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
interface FieldObject {
    field: Field;
    parent: HTMLElement;
    label: HTMLLabelElement;
    errorMessageElement?: HTMLElement | null;
}

export default class FormUI {
    protected form: HTMLFormElement;
    #hiddenFormClass = 'js-hidden';
    protected fieldObjects: FieldObject[];
    #phoneLibInstance: ReturnType<typeof intlTelInput> | null;
    #phoneField: HTMLInputElement | null;
    #useEmailDomainValidation: boolean;
    #submitButton;
    #recaptchaContainer: HTMLElement | null;
    #cm;

    constructor(el: HTMLFormElement) {
        this.form = el;
        this.fieldObjects = this.#getFieldObjects();
        this.#phoneLibInstance, this.#phoneField;
        this.#useEmailDomainValidation =
            this.form.dataset.restaurantLogin !== 'forms-login' &&
            window.location.pathname !==
                '/pos/restaurant/register-for-a-free-trial-sales/';
        this.#submitButton =
            this.form.querySelector<HTMLButtonElement>('[data-formui=button]');
        this.#recaptchaContainer = this.form.querySelector(
            '[data-formfield-error=recaptcha]'
        );
        this.#cm = new CookieMonster();
    }

    // This doesn't get fields that are of type 'hidden'
    #getFieldObjects() {
        const fields = Array.from(
            this.form.querySelectorAll<Field>(
                'input:not([type=hidden]), select, textarea'
            )
        );
        return fields.reduce((fieldObjects: FieldObject[], field) => {
            const parent = field.closest<HTMLElement>('[data-formfield]');
            const label = parent?.querySelector<HTMLLabelElement>(
                '.gb-form__field-label'
            );
            const errorMessageElement =
                parent?.querySelector<HTMLElement>('[data-form="error"]');
            if (!parent || !label) return fieldObjects;
            fieldObjects.push({
                parent: parent,
                label: label,
                field: field,
                errorMessageElement: errorMessageElement,
            });
            return fieldObjects;
        }, []);
    }

    #showForm() {
        this.form.classList.remove(this.#hiddenFormClass);
    }

    // This does initial validation if fields already have values, and adds event listeners for all fields to validate at appropriate moments
    #initializeFields() {
        this.fieldObjects.forEach(fieldObject => {
            const { field, errorMessageElement } = fieldObject;
            if (field.value !== '') {
                this.validateFieldObject(fieldObject);
                if (field instanceof HTMLInputElement) this.#raiseLabel(fieldObject);
            }
            if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
                switch (field.type) {
                    case 'select-one':
                    case 'select-multiple':
                        field.addEventListener('change', () => {
                            this.validateFieldObject(fieldObject);
                        });
                        break;
                    case 'text':
                    case 'email':
                    case 'password':
                    case 'tel':
                        if (field.type === 'tel') this.#initializePhoneField();
                        field.addEventListener(
                            'keydown',
                            debounce(() => {
                                this.validateFieldObject(fieldObject, true);
                            }, 700)
                        );
                        if (field.name === 'company') {
                            const domainPrefixFieldObject = this.fieldObjects.find(
                                fieldObject => fieldObject.field.name === 'domain_prefix'
                            );
                            if (domainPrefixFieldObject) {
                                field.addEventListener('change', async () => {
                                    if (domainPrefixFieldObject.field.value === '') {
                                        this.#setFieldValidity(
                                            domainPrefixFieldObject,
                                            true
                                        );
                                    }
                                    await this.#addSuggestedDomainPrefixValue(
                                        domainPrefixFieldObject,
                                        field
                                    );
                                });
                            }
                        }
                        break;
                    default:
                        break;
                }
            }
            field.addEventListener('focus', () => {
                if (field.value !== '') this.validateFieldObject(fieldObject);
                this.#raiseLabel(fieldObject);
            });
            field.addEventListener('blur', () => {
                this.validateFieldObject(fieldObject);
            });
            if (errorMessageElement) {
                errorMessageElement.id = `${field.name}-error-message`;
            }
        });
        if (this.#submitButton) {
            this.#submitButton.addEventListener('click', async (event: Event) => {
                event.preventDefault();
                await this.#handleSubmitButtonClick(event);
            });
        }
    }

    #initializePhoneField() {
        try {
            [this.#phoneLibInstance, this.#phoneField] = initPhoneLib(this.form);
            const phoneFieldObject = this.fieldObjects.find(
                fieldObject => fieldObject.field.name === 'phone'
            );
            if (phoneFieldObject && phoneFieldObject.errorMessageElement) {
                phoneFieldObject.field.insertAdjacentElement(
                    'afterend',
                    phoneFieldObject.errorMessageElement
                );
                const libContainer = phoneFieldObject.parent.querySelector<HTMLElement>(
                    '.iti__country-container'
                );
                if (libContainer && this.#phoneField)
                    libContainer.style.height = `${this.#phoneField.offsetHeight}px`;
            }
        } catch (e) {
            this.#phoneLibInstance?.destroy();
            LoggingHandler().logError(e, {
                searchTerm: 'phone-library-init-error',
                tags: ['forms', 'ui'],
                fileName: 'formUI.js',
                additionalData: {
                    url: window.location.href,
                },
            });
        }
    }

    /**
     * Validates the field.
     *
     * @param fieldObject - The object representing the field to validate.
     * @param softValidation - If true, performs a soft validation that does not mark the field as invalid.
     */
    protected async validateFieldObject(
        fieldObject: FieldObject,
        softValidation = false
    ) {
        const { field } = fieldObject;
        if (
            field instanceof HTMLInputElement ||
            field instanceof HTMLSelectElement ||
            field instanceof HTMLTextAreaElement
        ) {
            switch (field.type) {
                case 'text':
                case 'password':
                case 'textarea':
                    if (field.name === 'domain_prefix') {
                        await this.#validateDomainPrefixElement(
                            fieldObject,
                            softValidation
                        );
                    } else {
                        this.#validateTextInput(fieldObject, softValidation);
                    }
                    break;
                case 'tel':
                    this.#validatePhoneInput(fieldObject, softValidation);
                    break;
                case 'email':
                    await this.#validateEmailInput(fieldObject, softValidation);
                    break;
                case 'select-one':
                case 'select-multiple':
                    if (field.name === 'business_type') {
                        this.#validateBusinessTypeField(fieldObject);
                    } else {
                        this.#validateSelectElement(fieldObject);
                    }
                    break;
                default:
                    break;
            }

            if (field.checkValidity()) {
                this.#sendGoogleTagManagerFieldEvent(fieldObject);
            }
            if (this.form.checkValidity()) {
                this.form.dispatchEvent(
                    new CustomEvent('formFieldsValid', {
                        bubbles: true,
                        cancelable: true,
                        composed: false,
                    })
                );
            }
        }
    }

    #validateBusinessTypeField(businessTypeFieldObject: FieldObject) {
        const formName = this.form.dataset.formName;
        const { field } = businessTypeFieldObject;
        if (
            (formName?.includes('retail') || formName?.toLowerCase().includes('ecom')) &&
            formName.includes('trial') &&
            businessTypeFieldObject.field instanceof HTMLSelectElement &&
            businessTypeFieldObject.field.options[
                businessTypeFieldObject.field.selectedIndex
            ].hasAttribute('data-adp')
        ) {
            this.#setFieldValidity(businessTypeFieldObject, false, 'adp');
        } else {
            this.#validateSelectElement(businessTypeFieldObject);
        }

        if (field instanceof HTMLSelectElement) {
            handleDomainPrefixField({
                form: this.form,
            }).toggleDisplay();
        }
    }

    /*
     * @param e
     * */
    #sendGoogleTagManagerFieldEvent(fieldObject: FieldObject) {
        const { field } = fieldObject;
        // @ts-ignore
        gtm.field(this.form).sendEvent(`${field.name}|${getAnonymizedFieldValue(field)}`);
        const formGTMObjectConstructor = new FormGTMObjectConstructor(this.form);
        GoogleTagManager.sendEvent(
            formGTMObjectConstructor.getFormGTMObject(
                'field_completed',
                getAnonymizedFieldValue(field),
                field.name
            )
        );
    }

    #raiseLabel(fieldObject: FieldObject) {
        const raisedLabelClass = 'js-raised';
        const { field, label, parent } = fieldObject;

        // hack to prevent an empty select field with a default value from having its label raised
        if (field.value === 'default-role-field') return;

        // @email-hook condition
        // prevent raising label on inputs that don't have placeholder text (therefore there is no reason to raise the label, it will never be displaced by the placeholder text)
        if (
            'formName' in this.form.dataset &&
            this.form.dataset['formName']?.includes('email_hook') &&
            !parent.classList.contains('js-field-valid') &&
            (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
            !field.placeholder
        ) {
            if (label) label.classList.remove(raisedLabelClass);
            return;
        }
        // @email-hook ends

        label.classList.add('js-raised');
    }

    #customCheckValidity(input: Field) {
        // our own custom validity message makes the input automatically invalid,
        // so we temporarily disable that so that checkValidity() works
        // input.setCustomValidity('');
        return input.checkValidity();
    }

    /**
     * Validates a text input field.
     *
     * @param textFieldObject - The object representing the text field to validate.
     * @param softValidation - If true, performs a soft validation that does not mark the field as invalid.
     */
    #validateTextInput(textFieldObject: FieldObject, softValidation = false) {
        if (this.#customCheckValidity(textFieldObject.field)) {
            this.#setFieldValidity(textFieldObject, true);
        } else if (!softValidation) {
            this.#setFieldValidity(textFieldObject, false);
        }
    }

    /**
     * Validates the phone input field.
     *
     * @param phoneFieldObject - The object representing the phone field to validate.
     * @param softValidation - If true, performs a soft validation that does not mark the field as invalid.
     */
    #validatePhoneInput(phoneFieldObject: FieldObject, softValidation = false) {
        if (this.#phoneLibInstance) {
            const phoneFieldValidity = this.#phoneLibInstance.isValidNumber();
            if (phoneFieldValidity) {
                this.#setFieldValidity(phoneFieldObject, true);
            } else if (!softValidation) {
                this.#setFieldValidity(phoneFieldObject, false);
            }
        }
    }

    #setFormHeight() {
        if (this.form.dataset['formName']?.includes('email_hook')) return;

        const formHeight = isNaN(this.form.clientHeight)
            ? 'auto'
            : formConstants.errorMessageDefaultHeight + this.form.clientHeight;
        this.form.style.minHeight =
            formHeight && formHeight !== 'auto' ? `${formHeight}px` : 'auto';
    }

    #validateSelectElement(selectFieldObject: FieldObject) {
        const formName = this.form.dataset.formName;
        if (
            (formName?.includes('retail') || formName?.toLowerCase().includes('ecom')) &&
            formName.includes('trial') &&
            selectFieldObject.field instanceof HTMLSelectElement &&
            selectFieldObject.field.options[
                selectFieldObject.field.selectedIndex
            ].hasAttribute('data-adp')
        ) {
            this.#setFieldValidity(selectFieldObject, false, 'adp');
        } else if (selectFieldObject.field.value === '') {
            this.#setFieldValidity(selectFieldObject, false);
        } else {
            this.#setFieldValidity(selectFieldObject, true);
        }
    }

    /**
     * Validates the email input field.
     *
     * @param emailFieldObject - The object representing the email field to validate.
     * @param softValidation - If true, performs a soft validation that does not mark the field as invalid.
     */
    async #validateEmailInput(emailFieldObject: FieldObject, softValidation = false) {
        emailFieldObject.field.setCustomValidity('');
        if (this.#customCheckValidity(emailFieldObject.field)) {
            if (this.#useEmailDomainValidation) {
                const domain = emailFieldObject.field.value?.split('@')[1];
                const isValid = await this.#isValidEmailDomain(domain);
                if (isValid) {
                    this.#setFieldValidity(emailFieldObject, true);
                } else if (!softValidation) {
                    this.#setFieldValidity(emailFieldObject, false, 'email_domain');
                }
            } else {
                this.#setFieldValidity(emailFieldObject, true);
            }
        } else if (!softValidation) {
            this.#setFieldValidity(emailFieldObject, false);
        }
    }

    #isValidEmailDomain = async (domain: string) => {
        const endpoint = `https://dns.google/resolve?name=${domain}&type=mx`;
        const logErrorObject: Context = {
            searchTerm: 'email-validation-error',
            tags: ['forms', 'ui'],
            fileName: 'formUI.js',
            additionalData: {
                url: window.location.href,
                domain,
            },
        };
        const data = await doFetch(endpoint, logErrorObject);
        if (data && typeof data === 'object' && 'Status' in data) {
            const status = data?.Status;
            return status === 0;
        }
    };

    #isValidDomainPrefixElement = async (domainPrefixFieldObject: FieldObject) => {
        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

        const urlencoded = new URLSearchParams();
        urlencoded.append('domain', domainPrefixFieldObject.field.value);

        const requestOptions: RequestInit = {
            method: 'POST',
            headers: myHeaders,
            body: urlencoded,
            mode: 'no-cors',
            redirect: 'follow',
        };

        const endpoint = '/rest/forms/load/domain-prefix-validation';

        const data = await doFetch<string>(
            endpoint,
            {
                searchTerm: 'domain-prefix-validation-error',
                tags: ['forms', 'ui'],
                fileName: 'formUI.js',
                additionalData: {
                    url: window.location.href,
                    domain: domainPrefixFieldObject.field.value,
                },
            },
            undefined,
            undefined,
            requestOptions
        );
        if (data) {
            const parsedData = JSON.parse(data);
            return (
                'valid' in parsedData &&
                'available' in parsedData &&
                parsedData.valid &&
                parsedData.available
            );
        }
    };

    /**
     * Validates the domain prefix input field.
     *
     * @param domainPrefixFieldObject - The object representing the domain prefix field to validate.
     * @param softValidation - If true, performs a soft validation that does not mark the field as invalid.
     */
    #validateDomainPrefixElement = async (
        domainPrefixFieldObject: FieldObject,
        softValidation = false
    ) => {
        domainPrefixFieldObject.field.setCustomValidity('');
        if (domainPrefixFieldObject.field.checkValidity()) {
            const isValid = await this.#isValidDomainPrefixElement(
                domainPrefixFieldObject
            );
            if (isValid) {
                this.#setFieldValidity(domainPrefixFieldObject, true);
            } else {
                this.#setFieldValidity(
                    domainPrefixFieldObject,
                    false,
                    'unavailable_domain'
                );
            }
        } else if (!softValidation) {
            this.#setFieldValidity(domainPrefixFieldObject, false);
        }
    };

    async #addSuggestedDomainPrefixValue(
        domainPrefixFieldObject: FieldObject,
        companyField: Field
    ) {
        const domainPrefixField = domainPrefixFieldObject.field;
        if (companyField.value.length >= 5 && !domainPrefixField.value) {
            domainPrefixField.value = companyField.value
                .replaceAll(/[^a-zA-Z0-9]/g, '') // remove every character that is not a letter
                .toLowerCase();
            await this.#validateDomainPrefixElement(domainPrefixFieldObject);
        }
    }

    #setFieldValidity(
        fieldObject: FieldObject,
        inputIsValid: boolean,
        errorMessageSlug = 'default'
    ) {
        const { field, parent, errorMessageElement } = fieldObject;
        if (inputIsValid) {
            field.setCustomValidity('');
            field.setAttribute('aria-invalid', 'false');
            fieldObject.field.removeAttribute('aria-errormessage');
            field.dataset.valid = 'true';
            parent.classList.add('js-validated');
        } else {
            if (errorMessageElement) {
                let errorObject, errorMessage;
                const errorMessageDataAttribute =
                    errorMessageElement.dataset.formErrorMessages;
                if (errorMessageDataAttribute) {
                    errorObject = JSON.parse(errorMessageDataAttribute);
                }
                if (errorObject) {
                    errorMessage = errorObject[errorMessageSlug];
                    errorMessageElement.innerHTML = errorMessage;
                }

                if (errorMessageSlug !== 'default') field.setCustomValidity(errorMessage);
                field.setAttribute('aria-invalid', 'true');
                fieldObject.field.setAttribute(
                    'aria-errormessage',
                    `${field.name}-error-message`
                );
                field.dataset.valid = 'false';
            }
        }
    }

    #autofillFormsWithLocalStorageData() {
        let isFormAutoFilled = false;
        if (!isFormExcludedFromLocalStorage(this.form) && isEnabledFormAutofill()) {
            this.fieldObjects.forEach(fieldObject => {
                const { field } = fieldObject;
                const localStorageFieldValue = getDecodedFormFieldValue(
                    getFieldName(field)
                );
                if (localStorageFieldValue) {
                    isFormAutoFilled = true;
                    field.value = localStorageFieldValue;
                    // @email-hook condition
                    if (this.form.dataset['formName']?.includes('email_hook')) {
                        const formGTMObjectConstructor = new FormGTMObjectConstructor(
                            this.form
                        );
                        GoogleTagManager.sendEvent(
                            formGTMObjectConstructor.getFormGTMObject(
                                'field_completed',
                                getAnonymizedFieldValue(field),
                                getFieldName(field)
                            )
                        );
                        if (field.name === 'email') this.#openEmailHookDrawer();
                    }
                    // @email-hook ends
                    this.#raiseLabel(fieldObject);
                    this.#setFieldValidity(fieldObject, true);
                }
            });
            // TODO: implement gtm as typescript
            if (isFormAutoFilled) {
                // @ts-ignore
                gtm.form(this.form).sendEvent('autocomplete');
            }
        }
    }

    async #handleSubmitButtonClick(event: Event) {
        handleDomainPrefixField({
            form: this.form,
        }).removeOnSubmit();
        honeyPotField(this.form, event);

        const regularBusinessTypeField =
            this.form.querySelector<HTMLElement>('[id^=business_type]');
        const agnosticBusinessTypeField = this.form.querySelector<HTMLElement>(
            '[id^=agnostic_business_type]'
        );
        const businessTypeField = regularBusinessTypeField
            ? regularBusinessTypeField
            : agnosticBusinessTypeField;
        businessTypeField && this.#setBusinessTypeSlugField(businessTypeField);
        for (const fieldObject of this.fieldObjects) {
            await this.validateFieldObject(fieldObject);
        }
        const recaptchaField = this.form.querySelector(
            'textarea[name=g-recaptcha-response]'
        );
        // @ts-ignore
        recaptchaField && recaptchaFormFieldValidation(this.form, event);

        if (this.form.checkValidity()) {
            this.fieldObjects.find(fieldObject => {
                const { field } = fieldObject;
                if (field.name === 'phone') {
                    field.value = this.#phoneLibInstance?.getNumber() ?? '';
                }
            });
            const submitEvent = new Event('submit', {
                bubbles: true,
                cancelable: true,
            });
            this.form.dispatchEvent(submitEvent);
        } else {
            const firstInvalidFieldObject = this.fieldObjects.find(fieldObject => {
                if (fieldObject.field.checkValidity() === false) {
                    return true;
                }
            });
            if (firstInvalidFieldObject) {
                firstInvalidFieldObject.field.focus();
            }
        }
    }

    /* The field that gets set here is referred to in this file html/src/php/Pages/ThankYou/Request.php
    this is part of the X & K series launch, where we show different watch a demo video depending on the chosen business type
    https://jira.atlightspeed.net/browse/MW-4673 */
    #setBusinessTypeSlugField(businessTypeField: HTMLElement) {
        const slug = this.#getCurrentTargetSlug(businessTypeField);
        if (slug) {
            const businessTypeSlugField = this.form.querySelector<HTMLInputElement>(
                'input[name=business_type_slug]'
            );
            if (businessTypeSlugField) {
                businessTypeSlugField.value = slug;
            }
        }
    }

    #getCurrentTargetSlug(currentInput: HTMLElement) {
        if (currentInput instanceof HTMLSelectElement) {
            return currentInput.options[currentInput.selectedIndex]?.dataset.slug ?? '';
        } else {
            return '';
        }
    }

    /*
     * Loops through business type option tags that contain the `data-slug` attribute
     * compares the value of data-slug with the business types cookie.
     * If they are equal, it sets the business type value to the currently cookied business type
     * return {void}
     * */
    #autofillBusinessTypeWithCookieValue() {
        const businessTypeCookie = this.#cm.getCookie('ls_vertical_bt');
        const businessTypeField = this.form.querySelector<
            HTMLInputElement | HTMLSelectElement
        >('[id^=business_type]');
        const businessTypeOptionsWithSlug = businessTypeField
            ? businessTypeField.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
                  '[data-slug]'
              )
            : null;

        if (
            businessTypeCookie &&
            typeof businessTypeCookie === 'string' &&
            businessTypeOptionsWithSlug
        ) {
            const businessTypeOption = Array.from(businessTypeOptionsWithSlug).find(
                businessTypeOption =>
                    businessTypeOption.dataset.slug === businessTypeCookie
            );
            if (businessTypeOption && businessTypeField) {
                businessTypeField.value = businessTypeOption.value;
            }
        }
    }

    /*
     * Get initial country from geoConfig
     * */
    #autofillCountryFieldWithCookieValue = () => {
        const countrySelect = this.form.querySelector<HTMLSelectElement>(
            '[id$=user_set_country_code]'
        );
        const countryCookie =
            this.#cm.getCookie('geoConfig')?.country_code?.toLowerCase() ?? 'us';
        if (countrySelect !== null) {
            countrySelect.value = countryCookie.toUpperCase();
        }
    };

    // @email-hook functions
    #openEmailHookDrawer() {
        const emailHookModal = document.querySelector(
            '.js-leadform-email-hook__modal__form-content-wrapper'
        );
        if (emailHookModal) {
            this.form.style.height = `${this.form.scrollHeight}px`;
            emailHookModal.classList.remove(
                'js-leadform-email-hook__modal__form-content-wrapper--closed'
            );
            emailHookModal.classList.add(
                'js-leadform-email-hook__modal__form-content-wrapper--opened'
            );

            const disclaimerEl =
                emailHookModal.querySelector<HTMLElement>('.gb-form__disclaimer');
            if (disclaimerEl) disclaimerEl.style.display = 'block';
        }
        const allFields = Array.from(this.form.children);
        allFields.forEach(field => {
            field.removeAttribute('inert');
        });
    }

    #addContinueButtonValidationListener() {
        const continueButton = document.querySelector(
            '.js-leadform-email-hook__modal__form-content-wrapper__continue-button'
        );
        const emailFieldObject = this.fieldObjects.find(fieldObject => {
            return fieldObject.field.name === 'email';
        });
        if (continueButton && emailFieldObject) {
            continueButton.addEventListener('click', e => {
                e.preventDefault();
                this.#raiseLabel(emailFieldObject);
                this.#validateEmailInput(emailFieldObject);
                if (emailFieldObject.field.checkValidity()) {
                    this.#openEmailHookDrawer();
                }
            });
        }
    }

    loadFormSpecs() {
        if (this.#recaptchaContainer) {
            loadCaptcha(this.form, 'formload');
        } else {
            this.form.classList.remove(this.#hiddenFormClass);
        }

        this.#setFormHeight();
        handleZipCodeField(this.form);
        this.#autofillBusinessTypeWithCookieValue();
        this.#autofillCountryFieldWithCookieValue();
        this.#autofillFormsWithLocalStorageData();
        this.#showForm();
        this.#initializeFields();
        // @email-hook condition
        if (this.form.dataset['formName']?.includes('email_hook')) {
            this.#addContinueButtonValidationListener();
        }
        handleGooglePlacesFieldAutocomplete(
            this.fieldObjects,
            'address',
            setDataAttributeForAddress
        );
        handleGooglePlacesFieldAutocomplete(
            this.fieldObjects,
            'zip',
            setDataAttributeForZipCode
        );
    }
}
